import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { absolutizeUrl } from "@/components/ImagePicker";
import SEO from "@/components/SEO";
import DecorationLayer from "@/components/DecorationLayer";
import { SectionMark } from "@/components/PageHeader";
import ProductCard from "@/components/shop/ProductCard";
import { ARGENTARIO_COORDS } from "@/components/EditorialLabel";
import { ABNORMAL_ASSETS } from "@/components/AbnormalIllustrations";

const COPY = {
    it: {
        eyebrow: "ABNORMAL · STORY · MURPH · SHOP",
        intro1: "ABnormal è il pensiero, non il cervello.",
        intro2: "Parla di idee, curiosità e della strada che scegli tu. Indossiamo pensieri.",
        storyTitle: "Story",
        storyLead: "ABnormal Outdoor — Monte Argentario",
        story: [
            "ABnormal nasce da un gruppo di amici accomunati dalla passione per la mountain bike e l'enduro sul Monte Argentario.",
            "Uscite in compagnia, discese, trail e voglia di esplorare e far conoscere un territorio che offre tantissimo agli appassionati delle due ruote.",
            "Anche le nostre maglie sono nate così: da idee, battute e chiacchierate tra amici, spesso proprio durante le uscite sui trail. Idee che abbiamo deciso di trasformare in disegni da indossare.",
            "Da qui è cresciuto ABnormal, un progetto che unisce la passione per l'outdoor alla voglia di creare qualcosa di nostro.",
            "ABnormal — GROW DIFFERENT.",
        ],
        words: ["curiosità", "idee", "tentativi", "imparare", "errori", "esplorazione", "creatività", "individualità", "azione"],
        murphEyebrow: "Personaggio ricorrente",
        murphTitle: "Meet Murph",
        murphLead: "Murph è quello che prova per primo. Non perché sia bravo: perché è curioso.",
        murphBody: "Pessime idee, esperimenti, errori, progressione. Se una cosa può andare storta, Murph l'ha già provata. E ha imparato qualcosa.",
        murphQuote: "What could go wrong?",
        murphCta: "Leggi la storia di Murph",
        murphSoon: "Nuove tavole in arrivo",
        panel1: "Da qualche parte tra qui e chissà dove.",
        panel2: "Istruzioni di montaggio · v1.0",
        panel3: "Outtaway!",
        shopEyebrow: "Selezione · non tutto lo store",
        shopTitle: "Wear the thought",
        shopLead: "Lo shop ufficiale ABnormal vive qui: taglie, colori, checkout sicuro.",
        shopCta: "Entra nello shop",
        shopView: "Vedi",
        shopEmpty: "Selezione in arrivo.",
        back: "Torna ai trail",
        backSub: "L'Enduro viene prima. Sempre.",
    },
    en: {
        eyebrow: "ABNORMAL · STORY · MURPH · SHOP",
        intro1: "ABnormal is the thought, not the brain.",
        intro2: "It's about ideas, curiosity and choosing your own path. We wear thoughts.",
        storyTitle: "Story",
        storyLead: "ABnormal Outdoor — Monte Argentario",
        story: [
            "ABnormal was born from a group of friends who share a passion for mountain biking and enduro on Monte Argentario.",
            "Rides together, descents, trails and the urge to explore and share a territory that has so much to offer to two-wheel enthusiasts.",
            "Our tees were born the same way: from ideas, jokes and conversations between friends, often right during rides on the trails. Ideas we decided to turn into designs to wear.",
            "From there ABnormal grew, a project that brings together a passion for the outdoors and the desire to create something of our own.",
            "ABnormal — GROW DIFFERENT.",
        ],
        words: ["curiosity", "ideas", "trying", "learning", "mistakes", "exploration", "creativity", "individuality", "action"],
        murphEyebrow: "Recurring character",
        murphTitle: "Meet Murph",
        murphLead: "Murph is the one who tries first. Not because he's good: because he's curious.",
        murphBody: "Bad ideas, experiments, mistakes, progression. If something can go wrong, Murph has already tried it. And learned something.",
        murphQuote: "What could go wrong?",
        murphCta: "Read Murph's story",
        murphSoon: "New strips coming",
        panel1: "Somewhere between here and who knows where.",
        panel2: "Assembly instructions · v1.0",
        panel3: "Outtaway!",
        shopEyebrow: "Selected · not the whole store",
        shopTitle: "Wear the thought",
        shopLead: "The official ABnormal shop lives here: sizes, colours, secure checkout.",
        shopCta: "Enter the shop",
        shopView: "View",
        shopEmpty: "Selection coming soon.",
        back: "Back to the trails",
        backSub: "Enduro comes first. Always.",
    },
};


export default function Abnormal() {
    const { lang } = useLang();
    const c = COPY[lang === "en" ? "en" : "it"];
    const { hash } = useLocation();
    const [settings, setSettings] = useState({ shop_url: "/shop", murph_url: "/abnormal#murph", murph_images: [] });
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/settings").then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
        api.get("/shop/catalog").then((r) => setProducts((r.data || []).slice(0, 6))).catch(() => {});
    }, []);

    useEffect(() => {
        if (!hash) { window.scrollTo(0, 0); return; }
        const id = hash.replace("#", "");
        const tm = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
        return () => clearTimeout(tm);
    }, [hash]);

    const murphImgs = (settings.murph_images || []).filter(Boolean);

    return (
        <div data-testid="abnormal-page" className="bg-abnormal-ink text-abnormal-paper relative">
            <SEO
                title={lang === "en" ? "ABNORMAL · Story, Murph, Shop" : "ABNORMAL · Story, Murph, Shop"}
                description={lang === "en"
                    ? "ABnormal is the thought, not the brain. Grow different. Meet Murph. The official ABnormal shop, right here."
                    : "ABnormal è il pensiero, non il cervello. Grow different. Meet Murph. Lo shop ufficiale ABnormal, direttamente qui."}
                path="/abnormal"
            />
            <DecorationLayer page="abnormal" />

            {/* ═══ 01 · ABNORMAL ═══ */}
            <section id="abnormal" data-testid="abnormal-intro" className="relative border-b border-white/10 halftone overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-12 md:pb-20">
                    <div className="flex items-center gap-3 label-mono text-abnormal-paperDim/70 mb-6 flex-wrap">
                        <span className="text-abnormal-acid">[ 01 ]</span>
                        <span className="w-6 h-px bg-abnormal-acid/60" />
                        <span>{c.eyebrow}</span>
                        <span className="hidden sm:inline w-1 h-1 rounded-full bg-abnormal-acid" />
                        <span className="hidden sm:inline text-abnormal-acid">{ARGENTARIO_COORDS}</span>
                    </div>
                    <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end">
                        <div className="poster-in">
                            <h1 data-testid="abnormal-title" className="font-display uppercase leading-[0.82] tracking-tighter text-abnormal-paper misprint text-[76px] sm:text-[120px] md:text-[170px] lg:text-[210px]">
                                Abnormal<span className="text-abnormal-acid">.</span>
                            </h1>
                            <p className="mt-6 font-display uppercase text-2xl md:text-4xl leading-[0.95] tracking-tight text-abnormal-paper max-w-3xl">
                                {c.intro1} <span className="text-abnormal-paperDim">{c.intro2}</span>
                            </p>
                            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-display uppercase text-xl md:text-2xl tracking-tight">
                                <span className="text-abnormal-acid">Grow different.</span>
                                <span className="text-abnormal-paperDim">Normal gets left behind.</span>
                            </div>
                        </div>
                        <img src={ABNORMAL_ASSETS.brainMohawk} alt="Abnormal" width={260} loading="lazy" decoding="async"
                            className="hidden lg:block w-[220px] xl:w-[260px] h-auto select-none pointer-events-none -rotate-3 poster-in poster-delay-2" />
                    </div>
                </div>
            </section>

            {/* ═══ 02 · STORY ═══ */}
            <section id="story" data-testid="abnormal-story" className="relative border-b border-white/10 scroll-mt-16">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
                    <SectionMark n="02" title={c.storyTitle} right={<span className="label-mono text-abnormal-paperDim/60 hidden sm:inline">EST · MMXXVI</span>} />
                    <div className="grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-5">
                            <h2 className="font-display uppercase leading-[0.88] tracking-tighter text-abnormal-paper text-5xl md:text-7xl">
                                {c.storyLead}
                            </h2>
                            <ul className="mt-8 flex flex-wrap gap-2" data-testid="story-words">
                                {c.words.map((w, i) => (
                                    <li key={w} className={`label-mono px-2.5 py-1.5 border ${i % 4 === 0 ? "border-abnormal-acid/60 text-abnormal-acid" : "border-white/15 text-abnormal-paperDim"}`}>{w}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 space-y-5 text-abnormal-paperDim text-base md:text-lg leading-relaxed">
                            {c.story.map((p, i) => (
                                <p key={i} className={i === c.story.length - 1 ? "text-abnormal-paper font-display uppercase text-2xl md:text-3xl tracking-tight leading-tight pt-2" : ""}>{p}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══ 03 · MEET MURPH ═══ */}
            <section id="murph" data-testid="abnormal-murph" className="relative border-b border-white/10 scroll-mt-16 halftone">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
                    <SectionMark n="03" title={c.murphEyebrow} right={<span className="label-mono text-abnormal-acid hidden sm:inline">// {c.murphQuote}</span>} />
                    <h2 className="font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper text-[60px] sm:text-[90px] md:text-[130px]">
                        {c.murphTitle}<span className="text-abnormal-acid">.</span>
                    </h2>

                    {/* Comic strip — 1 wide panel + 2 portrait panels */}
                    <div data-testid="murph-panels" className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        {[c.panel1, c.panel2, c.panel3].map((cap, i) => {
                            const img = murphImgs[i];
                            const wide = i === 0;
                            return (
                                <figure key={i}
                                    className={`relative border-2 border-abnormal-paper overflow-hidden poster-in ${wide ? "md:col-span-2 aspect-[3/2]" : "aspect-[3/4] md:aspect-[4/5]"} ${i === 2 ? "bg-white" : "bg-abnormal-charcoal"}`}
                                    style={{ animationDelay: `${i * 90}ms` }}>
                                    {img ? (
                                        <img src={absolutizeUrl(img)} alt={cap} loading={wide ? "eager" : "lazy"} decoding="async"
                                            className={`absolute inset-0 w-full h-full ${wide ? "object-cover" : "object-contain"}`} />
                                    ) : (
                                        <div className="absolute inset-0 paper-grain bg-abnormal-paper text-black flex flex-col justify-end p-5 halftone">
                                            <span className="font-display uppercase leading-[0.85] text-5xl md:text-6xl tracking-tighter">{cap}</span>
                                        </div>
                                    )}
                                    <figcaption className="absolute top-3 left-3 bg-black text-abnormal-paper label-mono px-2 py-1 border border-abnormal-paper/40">
                                        PANEL {String(i + 1).padStart(2, "0")} / 03
                                    </figcaption>
                                    {img && (
                                        <span className="absolute bottom-3 left-3 right-3 md:right-auto bg-black/85 text-abnormal-paper font-display uppercase text-base md:text-lg leading-none px-3 py-1.5 border border-abnormal-paper/30">
                                            {cap}
                                        </span>
                                    )}
                                    {i === 2 && (
                                        <div className="absolute top-3 right-3 bg-abnormal-acid text-black font-display uppercase text-lg md:text-xl px-3 py-1 -rotate-2">
                                            {c.murphQuote}
                                        </div>
                                    )}
                                </figure>
                            );
                        })}
                    </div>

                    <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-7">
                            <p className="font-display uppercase text-2xl md:text-4xl leading-tight tracking-tight text-abnormal-paper">{c.murphLead}</p>
                            <p className="mt-4 text-abnormal-paperDim text-base md:text-lg leading-relaxed max-w-2xl">{c.murphBody}</p>
                        </div>
                        <div className="lg:col-span-5 lg:justify-self-end flex flex-col items-start lg:items-end gap-3">
                            {/^https?:/.test(settings.murph_url || "") ? (
                                <a href={settings.murph_url} target="_blank" rel="noreferrer" data-testid="murph-cta" className="btn-primary">{c.murphCta} <ArrowUpRight size={14} /></a>
                            ) : (
                                <a href="#murph" data-testid="murph-cta" className="btn-primary">{c.murphCta} <ArrowRight size={14} /></a>
                            )}
                            <span className="label-mono text-abnormal-paperDim/60">{c.murphSoon}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══ 04 · WEAR THE THOUGHT ═══ */}
            <section id="shop" data-testid="abnormal-shop" className="relative border-b border-white/10 scroll-mt-16">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
                    <SectionMark n="04" title={c.shopEyebrow} right={
                        <Link to="/shop" className="label-mono text-abnormal-acid inline-flex items-center gap-1 hover:underline underline-offset-4">
                            abnormalworld.it/shop <ArrowRight size={11} />
                        </Link>
                    } />
                    <div className="flex items-end justify-between gap-6 flex-wrap">
                        <div>
                            <h2 className="font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper text-[52px] sm:text-[80px] md:text-[110px]">
                                {c.shopTitle}<span className="text-abnormal-acid">.</span>
                            </h2>
                            <p className="mt-3 text-abnormal-paperDim text-base md:text-lg max-w-xl">{c.shopLead}</p>
                        </div>
                        <Link to="/shop" data-testid="shop-enter-cta-top" className="btn-primary">
                            {c.shopCta} <ArrowRight size={14} />
                        </Link>
                    </div>

                    {products.length === 0 ? (
                        <p className="mt-10 label-mono text-abnormal-paperDim/60 border border-white/10 p-8 text-center">{c.shopEmpty}</p>
                    ) : (
                        <div data-testid="featured-products" className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
                            {products.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
                        </div>
                    )}

                    <div className="mt-10 flex justify-center">
                        <Link to="/shop" data-testid="shop-enter-cta" className="btn-dark">
                            {c.shopCta} <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ═══ 05 · BACK TO THE TRAILS ═══ */}
            <section className="topo-lines">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14 flex items-center justify-between gap-6 flex-wrap">
                    <div>
                        <p className="label-mono text-abnormal-acid">// {c.backSub}</p>
                        <p className="mt-2 font-display uppercase text-3xl md:text-5xl tracking-tight leading-none text-abnormal-paper">Monte Argentario · Enduro MTB</p>
                    </div>
                    <Link to="/trail" data-testid="abnormal-back-trails" className="btn-ghost">
                        <ArrowLeft size={14} /> {c.back} <ArrowRight size={14} className="text-abnormal-acid" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
